/**
 * v3.99d.276 — Live Grow-Off homepage rotation
 * =============================================
 * Surfaces in_progress + judging grow-offs on the homepage, rotating one
 * at a time with their current leaderboard top-3 mini-card. Click drops
 * the visitor straight into the grow-off detail — they can apply to the
 * next round or buy seeds of the same genetic from the same breeder.
 *
 * Grow-Offs are COMPLETELY SEPARATE from Grow Logs (no shared data) —
 * this surface explicitly highlights the competitive layer.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Users, Calendar, ArrowRight, Crown, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/apiClient";

const STATUS_LABEL = {
  in_progress: "In Progress",
  judging: "Judging",
};

export default function LiveGrowOffsHero() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [boards, setBoards] = useState({}); // {growOffId: leaderboard items}
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/grow-offs", { params: { status: "in_progress", limit: 6 } }).catch(() => ({ data: { items: [] } })),
      api.get("/grow-offs", { params: { status: "judging", limit: 6 } }).catch(() => ({ data: { items: [] } })),
    ]).then(([a, b]) => {
      const merged = [...(a.data?.items || []), ...(b.data?.items || [])];
      setItems(merged);
    });
  }, []);

  // Eagerly load leaderboards for any items we'll rotate through.
  useEffect(() => {
    if (items.length === 0) return;
    const missing = items.filter(g => boards[g.id] === undefined);
    if (missing.length === 0) return;
    Promise.all(missing.map(g =>
      api.get(`/grow-offs/${g.slug || g.id}/leaderboard`).then(r => [g.id, r.data?.items || []]).catch(() => [g.id, []])
    )).then(rows => {
      setBoards(prev => {
        const next = { ...prev };
        rows.forEach(([id, arr]) => { next[id] = arr; });
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Auto-rotate every 8s; pause on hover/focus.
  useEffect(() => {
    if (items.length < 2 || paused) return;
    const t = setInterval(() => setActive(i => (i + 1) % items.length), 8000);
    return () => clearInterval(t);
  }, [items.length, paused]);

  if (items.length === 0) return null;

  const g = items[active];
  const board = (boards[g.id] || []).slice(0, 3);

  const prev = () => setActive(i => (i - 1 + items.length) % items.length);
  const next = () => setActive(i => (i + 1) % items.length);

  return (
    <section
      className="border-y border-[var(--border)]"
      style={{ background: "linear-gradient(180deg, var(--bg) 0%, var(--bg-2) 100%)" }}
      data-testid="live-grow-offs-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1400px] mx-auto px-6 py-12 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--gold)" }}>
              <Trophy className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Live grow-offs · Verified competitions
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter" style={{ color: "var(--text)" }}>
              Real growers. <span style={{ color: "var(--gold)" }}>Real trophies</span>.
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="w-10 h-10 inline-flex items-center justify-center border border-[var(--border)] hover:border-[var(--primary-text)]"
                  style={{ borderRadius: 2, color: "var(--text-2)" }}
                  data-testid="live-grow-offs-prev"
                  aria-label="Previous grow-off"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                  {active + 1} / {items.length}
                </span>
                <button
                  onClick={next}
                  className="w-10 h-10 inline-flex items-center justify-center border border-[var(--border)] hover:border-[var(--primary-text)]"
                  style={{ borderRadius: 2, color: "var(--text-2)" }}
                  data-testid="live-grow-offs-next"
                  aria-label="Next grow-off"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
            <Link
              to="/grow-offs"
              className="ml-2 text-[11px] font-mono uppercase tracking-[0.18em] underline"
              style={{ color: "var(--gold)" }}
              data-testid="live-grow-offs-see-all"
            >
              See all →
            </Link>
          </div>
        </div>

        <div
          className={
            "grid gap-0 bg-[var(--surface)] border border-[var(--border)] overflow-hidden " +
            (g.cover_image_url ? "lg:grid-cols-7" : "lg:grid-cols-5")
          }
          style={{ borderRadius: 2 }}
          data-testid={`live-grow-off-card-${g.id}`}
        >
          {/* v3.99d.278 — Cover image on the left (only when set). Layout
              expands to 7 cols (2 image + 3 main + 2 leaderboard) so the
              mini-leaderboard isn't squeezed out. */}
          {g.cover_image_url ? (
            <div className="lg:col-span-2 relative min-h-[220px] lg:min-h-0 overflow-hidden border-b lg:border-b-0 lg:border-r border-[var(--border)]" data-testid="live-grow-off-cover">
              <img src={g.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 100%)" }} />
            </div>
          ) : null}
          {/* Main column */}
          <div className="lg:col-span-3 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <span
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border"
                style={{ color: "#D4AF37", borderColor: "#D4AF3755", borderRadius: 2 }}
              >
                {STATUS_LABEL[g.status] || g.status}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
                {g.host_vendor_name ? `Hosted by ${g.host_vendor_name}` : "CannaForge-hosted"}
              </span>
            </div>
            <Link to={`/grow-offs/${g.slug || g.id}`} className="block group">
              <h3 className="font-display text-3xl md:text-4xl tracking-tighter group-hover:opacity-90" style={{ color: "var(--text)" }}>
                {g.title}
              </h3>
            </Link>
            {g.strain_name && (
              <div className="mt-2 text-base" style={{ color: "var(--gold)" }}>{g.strain_name}</div>
            )}
            {g.description && (
              <p className="mt-4 text-sm leading-relaxed line-clamp-3 max-w-xl" style={{ color: "var(--text-2)" }}>
                {g.description}
              </p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-4 text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
              <span><Users className="w-3 h-3 inline mr-1" />{g.participant_count}/{g.participant_limit} growers</span>
              {g.grow_end && (
                <span><Calendar className="w-3 h-3 inline mr-1" />Ends {new Date(g.grow_end).toLocaleDateString()}</span>
              )}
              {g.prize_details && <span>Prize: {g.prize_details.slice(0, 40)}{g.prize_details.length > 40 ? "…" : ""}</span>}
            </div>
            <div className="mt-7 flex flex-wrap gap-3" data-testid="live-grow-off-ctas">
              <Link to={`/grow-offs/${g.slug || g.id}`} className="btn-secondary">
                <Trophy className="w-4 h-4" /> View grow-off <ArrowRight className="w-4 h-4" />
              </Link>
              {g.listing_id && (
                <Link to={`/listing/${g.listing_id}`} className="btn-outline">
                  Buy these seeds
                </Link>
              )}
            </div>
          </div>

          {/* Leaderboard mini-card */}
          <div className="lg:col-span-2 p-6 md:p-8 border-l border-[var(--border)]" style={{ background: "var(--bg-2)" }} data-testid="live-grow-off-mini-leaderboard">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: "var(--gold)" }}>Top 3 standings</div>
            {board.length === 0 ? (
              <div className="text-sm italic" style={{ color: "var(--text-muted)" }}>No scores yet — early days.</div>
            ) : (
              <ol className="space-y-3">
                {board.map(r => (
                  <li
                    key={r.participant_id}
                    className="flex items-center gap-3"
                    data-testid={`live-grow-off-rank-${r.rank}`}
                  >
                    <span
                      className="font-mono text-xl w-8 shrink-0"
                      style={{ color: r.rank === 1 ? "var(--gold)" : "var(--text-muted)" }}
                    >
                      {r.rank === 1 ? <Crown className="w-5 h-5 inline" /> : `#${r.rank}`}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate" style={{ color: "var(--text)" }}>{r.user_name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
                        Score {r.weighted_score} · {r.community_votes} votes
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Dots */}
        {items.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-6" data-testid="live-grow-offs-dots">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Grow-off ${i + 1}`}
                className="w-1.5 h-1.5 rounded-full transition-all"
                style={{
                  background: i === active ? "var(--gold)" : "var(--border-strong)",
                  width: i === active ? 16 : 6,
                }}
                data-testid={`live-grow-offs-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
